/**
 * PWA update policy (ARCHITECTURE §8.8): a waiting service worker is activated only when the local
 * mission state is IDLE, no outbox entry is pending, and the user has been idle ≥ 30 s — the
 * client-side mirror of the deploy gate. A worker never activates mid-mission.
 */
import { browserClock } from '@lw/shared/clock/browser';
import type { Runtime } from '../runtime/runtime.js';

export function setupServiceWorker(rt: Runtime): void {
  // no service worker on loopback (development and the integration suite): it would pin a stale bundle
  if (!('serviceWorker' in navigator) || import.meta.env.DEV || location.hostname === '127.0.0.1' || location.hostname === 'localhost') return;
  let lastInput = browserClock.perfNow();
  for (const ev of ['pointerdown', 'keydown', 'touchstart']) window.addEventListener(ev, () => (lastInput = browserClock.perfNow()), { passive: true });
  import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const update = registerSW({
        immediate: true,
        onNeedRefresh() {
          rt.log('update downloaded — will install when the mission is over', 'warn');
          const gate = (): void => {
            const s = rt.client.snapshot;
            const idle = !s || s.state === 'IDLE' || s.state === 'SAVED' || s.state === 'DISCARDED';
            const quiet = rt.client.outbox.pending().length === 0 && browserClock.perfNow() - lastInput >= 30_000;
            if (idle && quiet) void update(true);
            else setTimeout(gate, 5_000);
          };
          gate();
        },
      });
    })
    .catch(() => undefined);
}
